'use client'

import { useCallback, useMemo, useState } from 'react'

import {
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { compact } from 'lodash'

import { QUERY_KEYS } from '~/constants/react-query'
import { REFETCH_MESSAGES } from '~/constants/refetch-intervals'
import { useWalletConnectContext } from '~/context/WalletConnectProvider'
import { useActiveOrgId } from '~/hooks/org'
import { useServerRequests } from '~/hooks/useRequests'
import { useSmartAccount, useSmartAccounts } from '~/hooks/useSmartAccounts'
import { useUserPasskeys } from '~/hooks/useUserPasskeys'
import { useAllChainIds } from '~/hooks/utils'
import { OrgMessage, OrgSmartAccount } from '~/types'
import { isTransactChainId } from '~/utils'
import { getMessageSignatureCount } from '~/utils/smartAccounts'

export const useMessage = (
  messageId: string,
): {
  message: OrgMessage | undefined
  isLoading: boolean
} => {
  const { serverGetRequest } = useServerRequests()
  const queryClient = useQueryClient()

  const { data: message, isLoading } = useQuery({
    queryKey: [QUERY_KEYS.MESSAGE, messageId],
    queryFn: async () => {
      const result = await serverGetRequest<{
        message: OrgMessage
      }>({
        path: `/api/smart_accounts/messages/${messageId}`,
      })

      if (!result?.data) {
        throw new Error('No message found')
      }

      return result.data.message
    },
    refetchInterval: REFETCH_MESSAGES,
    initialData: () => {
      const queries = compact(
        queryClient.getQueriesData({
          queryKey: [QUERY_KEYS.MESSAGES], // Matches all query keys with first element as QUERY_KEYS.MESSAGES
        }),
      ) as [string[], { pages: OrgMessage[][] }][]

      const message = queries
        .map(([, data]) => {
          if (!data?.pages) return []
          return data.pages.flat()
        })
        .flat()
        .find((message) => message.id === messageId)

      return message
    },
  })

  return {
    message,
    isLoading,
  }
}

const PAGE_SIZE = 20

export const useMessages = ({ smartAccountId }: { smartAccountId: string }) => {
  const { serverGetRequest } = useServerRequests()
  const activeOrgId = useActiveOrgId()
  const queryClient = useQueryClient()
  const chainIds = useAllChainIds().filter(isTransactChainId)
  const [hasMore, setHasMore] = useState(true)

  // TODO: Remove dependance on all smart accounts
  const { smartAccounts } = useSmartAccounts()
  const smartAccount = smartAccounts.find(
    (account) => account.id === smartAccountId,
  )
  const hasOrgAccess = smartAccount?.orgId === activeOrgId

  const params = useMemo(() => {
    const params: {
      limit: number
      chainIds: string
      orgId?: string
      smartAccountId: string
    } = {
      limit: PAGE_SIZE,
      chainIds: chainIds.join(','),
      smartAccountId,
    }
    if (activeOrgId && hasOrgAccess) {
      params['orgId'] = activeOrgId
    }
    return params
  }, [activeOrgId, chainIds, hasOrgAccess, smartAccountId])

  const fetchMessages = useCallback(
    async ({ pageParam }: { pageParam: number }) => {
      const result = await serverGetRequest<{
        messages: OrgMessage[]
      }>({
        path: `/api/smart_accounts/messages/`,
        params: {
          ...params,
          offset: pageParam * PAGE_SIZE,
        },
      })

      if (!result?.data) {
        throw new Error('No messages found')
      }

      // Pre-populate all messages in the cache
      result.data.messages.forEach((message) => {
        queryClient.setQueryData([QUERY_KEYS.MESSAGE, message.id], message)
      })

      if (result.data.messages.length < PAGE_SIZE) {
        setHasMore(false)
      }

      return result.data.messages
    },
    [params, queryClient, serverGetRequest],
  )

  const { data, isLoading, fetchNextPage } = useInfiniteQuery({
    queryKey: [QUERY_KEYS.MESSAGES, params],
    queryFn: fetchMessages,
    // NOTE: this will refetch all loaded pages every interval
    // Ideally we should only refetch the current visible page(s), as we are using a virtualized list
    refetchInterval: REFETCH_MESSAGES,
    initialPageParam: 0,
    getNextPageParam: (lastPage, _, lastPageParam) => {
      if (lastPage.length < PAGE_SIZE) {
        return null
      } else {
        return lastPageParam + 1
      }
    },
  })

  return {
    messages: data?.pages.flat() || [],
    hasMore,
    isInitialLoading: isLoading,
    fetchNextPage,
  }
}

export const useMessageProperties = (messageId: string) => {
  const { message, isLoading: isLoadingMessage } = useMessage(messageId)
  const { smartAccount, isLoading: isLoadingSmartAccount } = useSmartAccount(
    message?.smartAccountAddress,
  )
  const orgSmartAccount = smartAccount
    ? (smartAccount as OrgSmartAccount)
    : undefined
  const { passkeyIds, isLoading: isLoadingPasskeys } = useUserPasskeys()
  const { activeSessions, hasSubmittedMessage } = useWalletConnectContext()

  const isLoading =
    isLoadingMessage || isLoadingSmartAccount || isLoadingPasskeys

  if (isLoading) {
    return {
      isLoading,
      canSign: false,
      hasSigned: false,
    }
  }

  const isActiveSession = message
    ? activeSessions.some(
        (session) => session.topic === message.walletConnectSessionRequestTopic,
      )
    : false
  const signaturesNeeded =
    smartAccount!.threshold - getMessageSignatureCount(message!)

  const canSubmit =
    message &&
    isActiveSession &&
    signaturesNeeded <= 0 &&
    !hasSubmittedMessage(message.id)

  const canSign =
    orgSmartAccount &&
    signaturesNeeded > 0 &&
    orgSmartAccount.signers.some(
      (signer) =>
        passkeyIds.has(signer.id) &&
        message!.signers.every(
          (messageSigner) => messageSigner.id !== signer.id,
        ),
    )

  const hasSigned = message!.signers.some((signer) => passkeyIds.has(signer.id))

  const showSignature = hasSigned && !canSign && signaturesNeeded > 0

  return {
    isLoading,
    canSign,
    canSubmit,
    hasSigned,
    showSignature,
  }
}
